import { useId, type ComponentProps, type ReactNode } from "react";
import { Banner, Button, cn } from "@optymyse/suite-ui";
import { strings } from "../../lib/strings";

/** A page of the console: its column, as wide as its work needs, with the suite's spacing between its parts. */
export function Page({ wide = false, className, ...props }: ComponentProps<"div"> & { wide?: boolean }) {
  return (
    <div
      className={cn(
        "mx-auto flex w-full flex-col gap-6 px-6 py-8 max-md:px-4",
        wide ? "max-w-6xl" : "max-w-4xl",
        className,
      )}
      {...props}
    />
  );
}

/** A panel on the canvas, as the kit's tables sit: a part of a page, named by its own heading where it has one. */
export function Panel({
  title,
  aside,
  className,
  children,
  ...props
}: Omit<ComponentProps<"section">, "title"> & { title?: ReactNode; aside?: ReactNode }) {
  const heading = useId();
  return (
    <section
      aria-labelledby={title === undefined ? undefined : heading}
      className={cn("rounded-card border border-line bg-panel p-5 shadow-card max-md:p-4", className)}
      {...props}
    >
      {(title !== undefined || aside !== undefined) && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          {title !== undefined && (
            <h2 id={heading} className="text-heading text-ink">
              {title}
            </h2>
          )}
          {aside}
        </div>
      )}
      {children}
    </section>
  );
}

/** A record's properties: each label beside its value, wrapping under it on a phone. */
export function Properties({ children }: { children: ReactNode }) {
  return <dl className="divide-y divide-line">{children}</dl>;
}

/** One of a record's properties: its label, and its value or `None`. */
export function Property({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="grid gap-x-4 gap-y-0.5 py-2.5 first:pt-0 last:pb-0 sm:grid-cols-[11rem_minmax(0,1fr)]">
      <dt className="text-secondary text-ink-muted">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}

/** A property that is empty. */
export function None() {
  return <span className="text-ink-muted">{strings.customers.page.none}</span>;
}

/** The directory couldn't be read: what the Worker said, and a way to ask again. */
export function DirectoryProblem({ message, onRetry }: { message?: string; onRetry: () => void }) {
  return (
    <Banner
      tone="negative"
      action={
        <Button size="sm" variant="secondary" onClick={onRetry}>
          {strings.directory.retry}
        </Button>
      }
    >
      {message ?? strings.directory.unavailable}
    </Banner>
  );
}
